import { useEffect, useRef } from 'react';
import { Broadcast } from 'src/broadcast';
import { authQueries, useRequestsCountsQuery } from 'src/api/queries';
import { getObjectKeys } from 'src/helpers/getObjectKeys';
import { useQueryClient } from '@tanstack/react-query';
import { RequestStatus } from 'src/lib/services/api/request-api';
import { useLocation } from 'react-router-dom';

// Module-level refcount for the single shared `requestUpdatedByEditor` listener.
// `ModelEvent` holds exactly one listener slot, but `useRequestsCounts` mounts in
// many places at once (badges, dashboard, request pages, revisions). We register
// the listener while at least one consumer is mounted and remove it once the last
// one unmounts — so it can't be stomped by a per-instance cleanup, yet it doesn't
// leak or go stale after the original registering component unmounts.
let listenerRefCount = 0;

const useRequestsCounts = (...params: Parameters<typeof useRequestsCountsQuery>) => {
  const client = useQueryClient();

  const { data: counts, ...query } = useRequestsCountsQuery(...params);

  // Detect when the request page (revisions view) is closed based on URL path
  const { pathname } = useLocation();
  const isRevisionsRoute = /\/revisions\/?$/.test(pathname);

  const totalDrafts = counts?.draft.total.length ?? 0,
    newDrafts = counts?.draft.new.length ?? 0,
    totalQueued = counts?.queued.total.length ?? 0,
    newQueued = counts?.queued.new.length ?? 0,
    totalEditing = counts?.currently_editing.total.length ?? 0,
    newEditing = counts?.currently_editing.new.length ?? 0,
    totalDelivered = counts?.delivered.total.length ?? 0,
    newDelivered = counts?.delivered.new.length ?? 0,
    totalComplete = counts?.complete.total.length ?? 0,
    newComplete = counts?.complete.new.length ?? 0,
    totalNew = newDrafts + newQueued + newEditing + newDelivered + newComplete,
    totalAll = totalDrafts + totalQueued + totalEditing + totalDelivered + totalComplete;

  // All mutators below return a NEW top-level object (and new bucket
  // objects/arrays) rather than mutating `prev` in place. React Query compares
  // cache values by reference, so mutating-and-returning-`prev` left every
  // consumer (badge counts, "What Needs My Attention") stale until a refetch —
  // defeating the #2447/#2459 "update instantly without reload" goal.
  const add = (id: string, isDraft = false) => {
    query.setQueryData((prev) => {
      if (!prev) {
        return prev;
      }

      const next = { ...prev };
      if (isDraft) {
        next.draft = { ...prev.draft, total: [...prev.draft.total, id] };
      } else {
        next.queued = { ...prev.queued, total: [...prev.queued.total, id] };
      }

      return next;
    });
  };

  const update = (id: string, status: RequestStatus) => {
    query.setQueryData((prev) => {
      if (!prev) {
        return prev;
      }

      // Already in the target bucket — nothing to change.
      if (prev[status].total.includes(id)) {
        return prev;
      }

      const next = { ...prev };

      // Remove the id from whichever bucket currently holds it (same rule as
      // `remove`), then add it to the target status — all in one immutable
      // update instead of calling the `remove` setter recursively from inside
      // this updater.
      const statuses = getObjectKeys(prev);
      for (let i = 0; i < statuses.length; i++) {
        const key = statuses[i];
        const bucket = prev[key];
        if (!bucket) {
          continue;
        }

        if (bucket.total.includes(id)) {
          next[key] = {
            ...bucket,
            total: bucket.total.filter((r) => r !== id),
            new: bucket.new.filter((r) => r !== id),
          };

          break;
        }
      }

      next[status] = { ...next[status], total: [...next[status].total, id] };

      return next;
    });
  };

  const remove = (id: string) => {
    query.setQueryData((prev) => {
      if (!prev) {
        return prev;
      }

      const statuses = getObjectKeys(prev);
      for (let i = 0; i < statuses.length; i++) {
        const status = statuses[i];
        const bucket = prev[status];
        if (!bucket) {
          continue;
        }

        if (bucket.total.includes(id)) {
          const next = { ...prev };
          next[status] = {
            ...bucket,
            total: bucket.total.filter((r) => r !== id),
            new: bucket.new.filter((r) => r !== id),
          };

          return next;
        }
      }

      return prev;
    });
  };

  const review = (id: string, status?: RequestStatus) => {
    // Strip the unread/new flag from EVERY requestCounts cache variant, not just
    // this hook instance's params-specific key. The query key is
    // ['requestCounts', params], so each consumer reads a different cache entry:
    // the list/dashboard row badge uses `useRequestsCounts()` (params
    // `undefined`) and the tab counter uses `useRequestsCounts(filters)`, while
    // `review` itself is called from several places with mismatched params — the
    // Revisions page via `useRequestsCounts({})`, and request-page.tsx + the
    // request-form modals via `useRequestsCounts()` (`undefined`). With the old
    // instance-bound `query.setQueryData`, each caller cleared only its OWN
    // variant, so the badge stayed lit on the list/dashboard until a hard
    // refetch. `setQueriesData` on the shared key applies the same updater to
    // all variants so the badge clears instantly everywhere.
    client.setQueriesData<typeof counts>({ queryKey: [authQueries.requestCounts] }, (prev) => {
      if (!prev) {
        return prev;
      }

      if (status) {
        const bucket = prev[status];
        if (!bucket) {
          return prev;
        }

        const nextBucket = {
          ...bucket,
          new: bucket.new.filter((r) => r !== id),
          ...(bucket.unread_messages
            ? { unread_messages: bucket.unread_messages.filter((r) => r !== id) }
            : {}),
        };

        const next = { ...prev };
        next[status] = nextBucket;

        return next;
      }

      // Iterate all statuses. `new` lives under exactly one status, so we
      // strip from the first matching bucket only (original behaviour). But the
      // "What Needs My Attention" widget filters on `unread_messages`, and
      // that bucket can be populated under any status — so we always strip
      // the id from every status' unread_messages list. Karina's #2447
      // followup: opening a request had to clear the unread flag instantly
      // without a manual reload.
      const statuses = getObjectKeys(prev);
      let strippedFromNew = false;
      let changed = false;
      const next = { ...prev };

      for (let i = 0; i < statuses.length; i++) {
        const key = statuses[i];
        const bucket = prev[key];
        if (!bucket) {
          continue;
        }

        let nextBucket = bucket;

        if (!strippedFromNew && bucket.new.includes(id)) {
          nextBucket = { ...nextBucket, new: bucket.new.filter((r) => r !== id) };
          strippedFromNew = true;
        }

        if (bucket.unread_messages?.includes(id)) {
          nextBucket = {
            ...nextBucket,
            unread_messages: bucket.unread_messages.filter((r) => r !== id),
          };
        }

        if (nextBucket !== bucket) {
          next[key] = nextBucket;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  };

  useEffect(() => {
    // Ref-count the single shared listener (see the note on `listenerRefCount`
    // above): register on the first mounted consumer, remove on the last
    // unmount. The callback closes only over the app-stable `client`, so it
    // never goes stale — unlike the previous module-`let` guard, which bound
    // the listener to the first instance's `query.refetch` and silently stopped
    // refreshing counts once that instance unmounted.
    listenerRefCount += 1;
    if (listenerRefCount === 1) {
      Broadcast.user.requestUpdatedByEditor.setListener(({ id }) => {
        // Force refetch for the single-request + outcomes queries instead of
        // a soft `invalidateQueries`. Karina's #2459 repro: after the editor
        // flips a row from `needs_reedit` → `currently_editing` from admin
        // side, the master list updates but the status badge in the Revisions
        // page header stays stuck on the old value until a hard refresh.
        // The Revisions page subscribes to `[request, id]` via
        // `RequestQueryProvider` with `suspense: true, staleTime: 60s` — and
        // in React Query v4 invalidation's "auto-refetch active observers"
        // behavior doesn't always fire that suspense observer (the boundary
        // is between an active-with-data and a suspended-with-data state).
        // `refetchQueries` is unconditional and forces the network call, so
        // the header sees the new `status` immediately. Profile stays on
        // `invalidateQueries` because it's a non-suspense passive cache and
        // the next consumer will hit a fresh fetch.
        client.refetchQueries({ queryKey: [authQueries.request, id] });
        client.refetchQueries({ queryKey: [authQueries.requestOutcomes, id] });
        // Refresh the requests list family so the just-bumped row reorders
        // immediately (Karina's #2447: the dashboard widget held the row in its
        // old slot until the 30s poll). `invalidate`, not `refetch`: in RQ v4
        // invalidation overrides staleTime, so the mounted widget refetches even
        // inside its 15s window, AND it defaults to refetchType:'active' so only
        // on-screen lists hit the network — `refetchQueries` would also refetch
        // every inactive variant. (The [request, id] refetch above keeps the
        // unconditional form for the #2459 suspense-observer case.)
        client.invalidateQueries({ queryKey: [authQueries.requests] });
        client.invalidateQueries({ queryKey: [authQueries.profile] });
        // Counts refresh via the shared client (instance-independent) so badges
        // update regardless of which consumer registered the listener.
        client.invalidateQueries({ queryKey: [authQueries.requestCounts] });
      });
    }

    return () => {
      listenerRefCount -= 1;
      if (listenerRefCount === 0) {
        Broadcast.user.requestUpdatedByEditor.removeListener();
      }
    };
  }, [client]);

  // Invalidate counts when leaving the revisions route so badges refresh
  // immediately. Fire only on the revisions → non-revisions transition; the
  // previous version re-ran on every pathname change while *on* the route.
  const wasRevisionsRoute = useRef(false);
  useEffect(() => {
    if (wasRevisionsRoute.current && !isRevisionsRoute) {
      // Invalidate all variants of requestCounts (regardless of params)
      client.invalidateQueries({ queryKey: [authQueries.requestCounts] });
      client.invalidateQueries({ queryKey: [authQueries.requests] });
    }
    wasRevisionsRoute.current = isRevisionsRoute;
  }, [isRevisionsRoute, client]);

  return {
    counts,
    query,
    totalDrafts,
    newDrafts,
    totalQueued,
    newQueued,
    totalEditing,
    newEditing,
    totalDelivered,
    newDelivered,
    totalComplete,
    newComplete,
    totalNew,
    totalAll,
    review,
    add,
    update,
    remove,
  };
};

export { useRequestsCounts };
